import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_ENDPOINTS } from '../api/config';
import { useAuth } from './AuthContext';
import { applyOverdueStatus, normalizeStatus } from '../utils/assignments';
import { letterGradeToGPA, gpaToLetterGrade } from '../utils/gpa';
import { useReminderSystem } from '../hooks/useReminderSystem';
import { useGPA } from '../hooks/useGPA';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    theme: 'dark',
    aiModelPreference: 'flash-lite', // Default to Flash Lite model
    notifications: {
      email: true,
      push: true,
      dueDateReminders: true,
      reminderTime: 24
    },
    preferences: {
      defaultView: 'dashboard',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
      weekStart: 'sunday',
      semesterMiddleLabel: 'spring'
    },
    display: {
      compactMode: false,
      showCompletedAssignments: true,
      showUpcomingDeadlines: true,
      assignmentsPerPage: 20
    },
    gpa: {
      A: { min: 93, max: 100, value: 4.0 },
      'A-': { min: 90, max: 92, value: 3.67 },
      'B+': { min: 87, max: 89, value: 3.33 },
      B: { min: 83, max: 86, value: 3.0 },
      'B-': { min: 80, max: 82, value: 2.67 },
      'C+': { min: 77, max: 79, value: 2.33 },
      C: { min: 73, max: 76, value: 2.0 },
      'C-': { min: 70, max: 72, value: 1.67 },
      'D+': { min: 67, max: 69, value: 1.33 },
      D: { min: 60, max: 66, value: 1.0 },
      F: { min: 0, max: 59, value: 0.0 }
    }
  });
  const [reminderTracking, setReminderTracking] = useState({});

  // Ref to prevent multiple simultaneous fetches
  const fetchInProgressRef = useRef(false);
  // Ref to ensure initial load only happens once
  const initialLoadDoneRef = useRef(false);

  // Use custom hooks for complex functionality
  useReminderSystem(assignments, reminderTracking, setReminderTracking, isAuthenticated);
  const { parseGrade, formatGrade } = useGPA(assignments, courses, setCourses, settings);

  const normalizeAssignmentsStatuses = (assignmentList) => {
    return (assignmentList || []).map(assignment => ({
      ...assignment,
      status: normalizeStatus(assignment.status)
    }));
  };

  // Helper to handle API responses and check for auth errors
  const handleApiResponse = useCallback(async (response, context) => {
    if (response.status === 401) {
      // 401 after retry means authentication is truly invalid
      
      // Call logout to clear authentication state
      await logout();
      // Reload the page to reset the app state
      window.location.reload();
      throw new Error('Authentication expired. Please log in again.');
    }

    if (response.status === 403) {
      // Check if it's a free tier limit error
      try {
        const errorData = await response.clone().json();
        if (errorData.message && errorData.message.includes('Free tier limited')) {
          // Show upgrade prompt for free tier limits
          alert(`${errorData.message}\n\nUpgrade to premium for unlimited access!`);

          // Prevent double errors by stopping silently
          return Promise.reject();
        }
      } catch (parseError) {
        // Ignore parse errors
      }
    }

    return response;
  }, [logout]);

  // This effect is now the single source of truth for all data fetching.
  // It runs when authentication status changes.
  useEffect(() => {
    // Reset initial load flag when auth changes
    initialLoadDoneRef.current = false;

    const prefetchData = async () => {
      if (!isAuthenticated || fetchInProgressRef.current) {
        setLoading(false);
        return;
      }

      fetchInProgressRef.current = true;
      setLoading(true);
      
      const fetchWithRetry = async (url, options, retries = 3, delay = 1000) => {
        for (let i = 0; i < retries; i++) {
          try {
            const response = await fetch(url, {
              ...options,
              credentials: 'include'
            });
            if (response.ok) {
              return response;
            }
            // Special handling for 401 on first attempt
            if (response.status === 401 && i === 0) {
              continue; // Retry immediately, assuming token is being renewed
            }
            if (i < retries - 1) {
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          } catch (error) {
            if (i < retries - 1) {
              await new Promise(resolve => setTimeout(resolve, delay));
            } else {
              throw error;
            }
          }
        }
        // Return the last failed response if all retries fail
        return fetch(url, { ...options, credentials: 'include' });
      };
      
      try {
        const results = await Promise.allSettled([
          fetchWithRetry(API_ENDPOINTS.COURSES, {}),
          fetchWithRetry(API_ENDPOINTS.ASSIGNMENTS, {}),
          fetchWithRetry(API_ENDPOINTS.SETTINGS, {}),
          fetchWithRetry(API_ENDPOINTS.NOTIFICATIONS, {}),
          fetchWithRetry(API_ENDPOINTS.REMINDER_TRACKING, {})
        ]);

        const [
          coursesResult,
          assignmentsResult,
          settingsResult,
          notificationsResult,
          reminderResult
        ] = results;

        // Process courses
        if (coursesResult.status === 'fulfilled' && coursesResult.value.ok) {
          const data = await coursesResult.value.json();
          const apiCourses = data.courses || [];
          // Check if we should add test course (not for production/dev URLs)
          const isProductionOrDev = typeof window !== 'undefined' && 
            (window.location.hostname === 'dev.planmysemester.xyz' || 
             window.location.hostname === 'planmysemester.xyz');
          
          if (!isProductionOrDev) {
            // Add test course for development
            const testCourse = {
              _id: 'test-course-1',
              name: 'Computer Science 101',
              course_code: 'CS101',
              color: '#3B82F6',
              description: 'Introduction to Computer Science fundamentals',
              credits: 3,
              semester: 'Spring',
              year: 2026,
              instructor: 'Dr. Smith'
            };
            setCourses([testCourse, ...apiCourses]);
          } else {
            setCourses(apiCourses);
          }
        } else {
          console.error('Failed to fetch courses:', coursesResult.reason || coursesResult.value.status);
          // Check if we should add test course (not for production/dev URLs)
          const isProductionOrDev = typeof window !== 'undefined' && 
            (window.location.hostname === 'dev.planmysemester.xyz' || 
             window.location.hostname === 'planmysemester.xyz');
          
          if (!isProductionOrDev) {
            // Still add test course even if API fails
            const testCourse = {
              _id: 'test-course-1',
              name: 'Computer Science 101',
              course_code: 'CS101',
              color: '#3B82F6',
              description: 'Introduction to Computer Science fundamentals',
              credits: 3,
              semester: 'Fall',
              year: 2025,
              instructor: 'Dr. Smith'
            };
            setCourses([testCourse]);
          } else {
            setCourses([]);
          }
        }

        // Process assignments
        if (assignmentsResult.status === 'fulfilled' && assignmentsResult.value.ok) {
          const data = await assignmentsResult.value.json();
          const apiAssignments = data.assignments || [];
          // Check if we should add test assignments (not for production/dev URLs)
          const isProductionOrDev = typeof window !== 'undefined' && 
            (window.location.hostname === 'dev.planmysemester.xyz' || 
             window.location.hostname === 'planmysemester.xyz');
          
          if (!isProductionOrDev) {
            // Add test assignments for development
            const testAssignments = [
              {
                _id: 'test-assignment-1',
                title: 'Introduction to Programming',
                description: 'Learn the basics of programming with Python. Cover variables, data types, and basic control structures.',
                due_date: '2025-01-15T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'high',
                status: 'pending',
                createdAt: '2025-01-01T00:00:00.000Z'
              },
              {
                _id: 'test-assignment-2',
                title: 'Data Structures Assignment',
                description: 'Implement basic data structures including arrays, linked lists, and stacks.',
                due_date: '2025-01-22T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'medium',
                status: 'in_progress',
                createdAt: '2025-01-05T00:00:00.000Z'
              },
              {
                _id: 'test-assignment-3',
                title: 'Algorithm Analysis',
                description: 'Analyze the time and space complexity of various sorting and searching algorithms.',
                due_date: '2025-02-01T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'high',
                status: 'pending',
                createdAt: '2025-01-10T00:00:00.000Z'
              },
              {
                _id: 'test-assignment-4',
                title: 'Database Design Project',
                description: 'Design and implement a relational database for a library management system.',
                due_date: '2025-02-10T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'low',
                status: 'completed',
                createdAt: '2025-01-15T00:00:00.000Z'
              },
              {
                _id: 'test-assignment-5',
                title: 'Final Project Proposal',
                description: 'Submit a proposal for the semester-long final project.',
                due_date: '2025-02-20T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'high',
                status: 'pending',
                createdAt: '2025-01-20T00:00:00.000Z'
              }
            ];
            setAssignments(applyOverdueStatus(normalizeAssignmentsStatuses([...testAssignments, ...apiAssignments])));
          } else {
            setAssignments(applyOverdueStatus(normalizeAssignmentsStatuses(apiAssignments)));
          }
        } else {
          console.error('Failed to fetch assignments:', assignmentsResult.reason || assignmentsResult.value.status);
          // Check if we should add test assignments (not for production/dev URLs)
          const isProductionOrDev = typeof window !== 'undefined' && 
            (window.location.hostname === 'dev.planmysemester.xyz' || 
             window.location.hostname === 'planmysemester.xyz');
          
          if (!isProductionOrDev) {
            // Still add test assignments even if API fails
            const testAssignments = [
              {
                _id: 'test-assignment-1',
                title: 'Introduction to Programming',
                description: 'Learn the basics of programming with Python. Cover variables, data types, and basic control structures.',
                due_date: '2025-01-15T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'high',
                status: 'pending',
                createdAt: '2025-01-01T00:00:00.000Z'
              },
              {
                _id: 'test-assignment-2',
                title: 'Data Structures Assignment',
                description: 'Implement basic data structures including arrays, linked lists, and stacks.',
                due_date: '2025-01-22T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'medium',
                status: 'in_progress',
                createdAt: '2025-01-05T00:00:00.000Z'
              },
              {
                _id: 'test-assignment-3',
                title: 'Algorithm Analysis',
                description: 'Analyze the time and space complexity of various sorting and searching algorithms.',
                due_date: '2025-02-01T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'high',
                status: 'pending',
                createdAt: '2025-01-10T00:00:00.000Z'
              },
              {
                _id: 'test-assignment-4',
                title: 'Database Design Project',
                description: 'Design and implement a relational database for a library management system.',
                due_date: '2025-02-10T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'low',
                status: 'completed',
                createdAt: '2025-01-15T00:00:00.000Z'
              },
              {
                _id: 'test-assignment-5',
                title: 'Final Project Proposal',
                description: 'Submit a proposal for the semester-long final project.',
                due_date: '2025-02-20T23:59:00.000Z',
                course: 'test-course-1',
                priority: 'high',
                status: 'pending',
                createdAt: '2025-01-20T00:00:00.000Z'
              }
            ];
            setAssignments(applyOverdueStatus(normalizeAssignmentsStatuses(testAssignments)));
          } else {
            setAssignments([]);
          }
        }

        // Process settings
        if (settingsResult.status === 'fulfilled' && settingsResult.value.ok) {
          const data = await settingsResult.value.json();
          if (data.settings) {
            setSettings(prev => ({ ...prev, ...data.settings }));
          }
        } else {
          console.error('Failed to fetch settings:', settingsResult.reason || settingsResult.value.status);
        }

        // Process notifications
        if (notificationsResult.status === 'fulfilled' && notificationsResult.value.ok) {
          const data = await notificationsResult.value.json();
          setNotifications(data.notifications || []);
        } else {
          console.error('Failed to fetch notifications:', notificationsResult.reason || notificationsResult.value.status);
        }

        // Process reminder tracking
        if (reminderResult.status === 'fulfilled' && reminderResult.value.ok) {
          const data = await reminderResult.value.json();
          setReminderTracking(data.reminderTracking || {});
        } else {
          console.error('Failed to fetch reminder tracking:', reminderResult.reason || reminderResult.value.status);
        }

      } catch (error) {
        console.error('Error prefetching data:', error);
      } finally {
        fetchInProgressRef.current = false;
        setLoading(false);
      }
    };

    if (!isAuthenticated) {
      setCourses([]);
      setAssignments([]);
      setNotifications([]);
      setReminderTracking({});
      setLoading(false);
    } else if (!initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      prefetchData();
    }
  }, [isAuthenticated, handleApiResponse]);

  // The prefetchData function is now exposed for manual refresh if needed,
  // but it's the same logic as the initial load.
  const prefetchData = useCallback(async () => {
    if (!isAuthenticated || fetchInProgressRef.current) return;

    fetchInProgressRef.current = true;
    setLoading(true);
    
    const fetchWithRetry = async (url, options, retries = 3, delay = 1000) => {
      for (let i = 0; i < retries; i++) {
        try {
          const response = await fetch(url, {
            ...options,
            credentials: 'include'
          });
          if (response.ok) {
            return response;
          }
          if (response.status === 401 && i === 0) {
            continue;
          }
          if (i < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        } catch (error) {
          if (i < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            throw error;
          }
        }
      }
      return fetch(url, { ...options, credentials: 'include' });
    };
    
    try {
      const results = await Promise.allSettled([
        fetchWithRetry(API_ENDPOINTS.COURSES, {}),
        fetchWithRetry(API_ENDPOINTS.ASSIGNMENTS, {}),
        fetchWithRetry(API_ENDPOINTS.SETTINGS, {}),
        fetchWithRetry(API_ENDPOINTS.NOTIFICATIONS, {}),
        fetchWithRetry(API_ENDPOINTS.REMINDER_TRACKING, {})
      ]);

      const [
        coursesResult,
        assignmentsResult,
        settingsResult,
        notificationsResult,
        reminderResult
      ] = results;

      if (coursesResult.status === 'fulfilled' && coursesResult.value.ok) {
        const data = await coursesResult.value.json();
        setCourses(data.courses || []);
      }

      if (assignmentsResult.status === 'fulfilled' && assignmentsResult.value.ok) {
        const data = await assignmentsResult.value.json();
        setAssignments(applyOverdueStatus(normalizeAssignmentsStatuses(data.assignments || [])));
      }

      if (settingsResult.status === 'fulfilled' && settingsResult.value.ok) {
        const data = await settingsResult.value.json();
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }));
        }
      }

      if (notificationsResult.status === 'fulfilled' && notificationsResult.value.ok) {
        const data = await notificationsResult.value.json();
        setNotifications(data.notifications || []);
      }

      if (reminderResult.status === 'fulfilled' && reminderResult.value.ok) {
        const data = await reminderResult.value.json();
        setReminderTracking(data.reminderTracking || {});
      }
    } catch (error) {
      console.error('Error during manual prefetch:', error);
    } finally {
      fetchInProgressRef.current = false;
      setLoading(false);
    }
  }, [isAuthenticated]);

  const addCourse = useCallback(async (course) => {
    if (!isAuthenticated) return;

    // Input validation
    if (!course || typeof course !== 'object') {
      console.error('Invalid course data: course must be an object');
      return;
    }
    if (!course.name || typeof course.name !== 'string' || course.name.trim().length === 0) {
      console.error('Invalid course data: course name is required');
      return;
    }
    if (course.credits !== undefined && (typeof course.credits !== 'number' || course.credits < 0)) {
      console.error('Invalid course data: credits must be a non-negative number');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.COURSES, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(course)
      });

      await handleApiResponse(response, 'addCourse');

      if (response.ok) {
        const newCourse = await response.json();
        // Validate the returned course data
        if (newCourse.course && newCourse.course._id) {
          setCourses([...courses, newCourse.course]);
          // Note: Removed prefetchData() call to prevent interference with optimistic update
        } else {
          console.error('Invalid course data received from server');
        }
      } else {
        // Handle non-403 errors (like validation errors)
        const errorData = await response.json().catch(() => ({ message: 'Failed to add course' }));
        alert(`Error adding course: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding course:', error);
      // Only show alert for non-free-tier-limit errors
      if (error && error.message && !error.message.includes('Free tier limited')) {
        alert(`Error adding course: ${error.message}`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- assignments is intentionally referenced for updating related assignments
  }, [isAuthenticated, courses, setCourses, handleApiResponse, assignments]);

  const updateCourse = useCallback(async (id, updatedCourse) => {
    if (!isAuthenticated) return;

    // Input validation
    if (!id || typeof id !== 'string') {
      console.error('Invalid course ID: must be a non-empty string');
      return;
    }
    if (!updatedCourse || typeof updatedCourse !== 'object') {
      console.error('Invalid course data: updatedCourse must be an object');
      return;
    }
    if (updatedCourse.name !== undefined && (typeof updatedCourse.name !== 'string' || updatedCourse.name.trim().length === 0)) {
      console.error('Invalid course data: course name cannot be empty if provided');
      return;
    }
    if (updatedCourse.credits !== undefined && (typeof updatedCourse.credits !== 'number' || updatedCourse.credits < 0)) {
      console.error('Invalid course data: credits must be a non-negative number if provided');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.COURSE_BY_ID(id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updatedCourse)
      });

      await handleApiResponse(response, 'updateCourse');

      if (response.ok) {
        const updated = await response.json();
        // Validate the returned course data
        if (updated.course && updated.course._id) {
          const nextCourses = (courses || []).map(c => c._id === id ? updated.course : c);
          const completedBefore = (courses || []).filter(c => c.status === 'completed').length;
          const completedAfter = nextCourses.filter(c => c.status === 'completed').length;
          console.debug('[DataContext] updateCourse - id:', id, 'statusAfter:', updated.course.status, 'completedBefore:', completedBefore, 'completedAfter:', completedAfter);
          setCourses(nextCourses);

        } else {
          console.error('Invalid course data received from server');
        }

        // Update all assignments for this course to inherit the new color
        if (updated.course.color) {
          const courseAssignments = (assignments || []).filter(a => a.course && (a.course._id === id || a.course === id));
          if (courseAssignments.length > 0) {
            // Update assignments in the backend
            const updatePromises = courseAssignments.map(async (assignment) => {
              try {
                const updateResponse = await fetch(API_ENDPOINTS.ASSIGNMENT_BY_ID(assignment._id), {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  credentials: 'include',
                  body: JSON.stringify({ color: updated.color })
                });
                return updateResponse.ok ? await updateResponse.json() : null;
              } catch (error) {
                console.error('Error updating assignment color:', error);
                return null;
              }
            });

            const updatedAssignments = await Promise.all(updatePromises);
            const validUpdates = updatedAssignments.filter(a => a !== null);

            // Update local state
            setAssignments((assignments || []).map(a => {
              const updatedAssignment = validUpdates.find(ua => ua._id === a._id);
              return updatedAssignment || a;
            }));
          }
        }
        // Note: Removed prefetchData() call to prevent interference with optimistic update
      }
    } catch (error) {
      console.error('Error updating course:', error);
    }
  }, [isAuthenticated, courses, setCourses, assignments, setAssignments, handleApiResponse]);

  const deleteCourse = useCallback(async (id) => {
    if (!isAuthenticated) return;

    // Input validation
    if (!id || typeof id !== 'string') {
      console.error('Invalid course ID: must be a non-empty string');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.COURSE_BY_ID(id), {
        method: 'DELETE',
        credentials: 'include'
      });

      await handleApiResponse(response, 'deleteCourse');

      if (response.ok) {
        // Update courses state
        setCourses((courses || []).filter(c => c._id !== id));
        // Also update assignments state to remove assignments for this course
        setAssignments((assignments || []).filter(a => a.course?._id !== id && a.course !== id));
        // Note: Removed prefetchData() call to prevent interference with optimistic update
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete course');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      throw error;
    }
  }, [isAuthenticated, courses, setCourses, assignments, setAssignments, handleApiResponse]);

  const addAssignment = useCallback(async (assignment) => {
    if (!isAuthenticated) return;

    // Input validation
    if (!assignment || typeof assignment !== 'object') {
      console.error('Invalid assignment data: assignment must be an object');
      return;
    }
    if (!assignment.title || typeof assignment.title !== 'string' || assignment.title.trim().length === 0) {
      console.error('Invalid assignment data: assignment title is required');
      return;
    }
    if (!assignment.course && assignment.course !== null && (!assignment.course || (typeof assignment.course !== 'string' && (!assignment.course._id || typeof assignment.course._id !== 'string')))) {
      console.error('Invalid assignment data: valid course reference is required');
      return;
    }
    if (!assignment.due_date || isNaN(new Date(assignment.due_date).getTime())) {
      console.error('Invalid assignment data: valid due_date is required');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ASSIGNMENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(assignment)
      });

      await handleApiResponse(response, 'addAssignment');

      if (response.ok) {
        const newAssignment = await response.json();
        // Validate the returned assignment data
        if (newAssignment.assignment && newAssignment.assignment._id) {
          setAssignments(applyOverdueStatus(normalizeAssignmentsStatuses([...assignments, newAssignment.assignment])));
          // Note: Removed prefetchData() call to prevent interference with optimistic update
        } else {
          console.error('Invalid assignment data received from server');
        }
      } else {
        // Handle non-403 errors (like validation errors)
        const errorData = await response.json().catch(() => ({ message: 'Failed to add assignment' }));
        alert(`Error adding assignment: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding assignment:', error);
      // Only show alert for non-free-tier-limit errors
      if (error && error.message && !error.message.includes('Free tier limited')) {
        alert(`Error adding assignment: ${error.message}`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- prefetchData is stable via useCallback; include it in deps where needed but suppress warning here
  }, [isAuthenticated, assignments, setAssignments, handleApiResponse, prefetchData]);

  const updateAssignment = useCallback(async (id, updatedAssignment) => {
    if (!isAuthenticated) return;

    // Input validation
    if (!id || typeof id !== 'string') {
      console.error('Invalid assignment ID: must be a non-empty string');
      return;
    }
    if (!updatedAssignment || typeof updatedAssignment !== 'object') {
      console.error('Invalid assignment data: updatedAssignment must be an object');
      return;
    }
    if (updatedAssignment.name !== undefined && (typeof updatedAssignment.name !== 'string' || updatedAssignment.name.trim().length === 0)) {
      console.error('Invalid assignment data: assignment name cannot be empty if provided');
      return;
    }
    if (updatedAssignment.due_date !== undefined && updatedAssignment.due_date !== null && isNaN(new Date(updatedAssignment.due_date).getTime())) {
      console.error('Invalid assignment data: due_date must be a valid date if provided');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ASSIGNMENT_BY_ID(id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updatedAssignment)
      });

      await handleApiResponse(response, 'updateAssignment');

      if (response.ok) {
        const updated = await response.json();
        // Validate the returned assignment data
        if (updated.assignment && updated.assignment._id) {
          // Compute next assignments state (so we can log and avoid race conditions)
          const nextAssignments = applyOverdueStatus(normalizeAssignmentsStatuses((assignments || []).map(a => a._id === id ? updated.assignment : a)));
          const totalBefore = (assignments || []).length;
          const completedBefore = (assignments || []).filter(a => a.status === 'completed').length;
          const totalAfter = nextAssignments.length;
          const completedAfter = nextAssignments.filter(a => a.status === 'completed').length;
          console.debug('[DataContext] updateAssignment - id:', id, 'statusBefore:', (assignments || []).find(a => a._id === id)?.status, 'statusAfter:', updated.assignment.status);
          console.debug('[DataContext] updateAssignment - counts before:', { totalBefore, completedBefore }, 'after:', { totalAfter, completedAfter });

          setAssignments(nextAssignments);
          
        } else {
          console.error('Invalid assignment data received from server');
        }
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  }, [isAuthenticated, assignments, setAssignments, handleApiResponse]);

  const deleteAssignment = useCallback(async (id) => {
    if (!isAuthenticated) return;

    // Input validation
    if (!id || typeof id !== 'string' || id.trim() === '') {
      console.error('Invalid assignment ID: must be a non-empty string');
      throw new Error('Invalid assignment ID');
    }

    try {
      const response = await fetch(API_ENDPOINTS.ASSIGNMENT_BY_ID(id), {
        method: 'DELETE',
        credentials: 'include'
      });

      await handleApiResponse(response, 'deleteAssignment');

      if (response.ok) {
        setAssignments((assignments || []).filter(a => a._id !== id));
        // Note: Removed prefetchData() call to prevent interference with optimistic update
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete assignment');
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      throw error;
    }
  }, [isAuthenticated, assignments, setAssignments, handleApiResponse]);

  const addNotification = useCallback((notification) => {
    setNotifications(prev => [...prev, { ...notification, id: Date.now() }]);
  }, []);
  
  const removeNotification = useCallback((id) => setNotifications(notifications.filter(n => n.id !== id)), [notifications, setNotifications]);

  // Settings management
  const updateSettings = useCallback(async (newSettings) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);

    // Always persist settings to backend when authenticated
    if (isAuthenticated) {
      try {
        const response = await fetch(API_ENDPOINTS.SETTINGS, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ settings: updatedSettings })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Failed to update settings:', response.status, errorData);
          // Revert settings on failure
          setSettings(settings);
          throw new Error(errorData.message || 'Failed to update settings');
        }

        const data = await response.json();
        if (data.success) {
          // Update with the response data to ensure consistency
          setSettings(data.settings || updatedSettings);
        } else {
          console.error('Settings update failed:', data.message);
          // Revert settings on failure
          setSettings(settings);
          throw new Error(data.message || 'Failed to update settings');
        }
      } catch (error) {
        console.error('Error updating settings:', error);
        // Revert settings on error
        setSettings(settings);
        throw error;
      }
    }
  }, [isAuthenticated, settings, setSettings]);

  const updateSetting = useCallback(async (path, value) => {
    if (!path || typeof path !== 'string') {
      throw new Error('Invalid path provided to updateSetting');
    }

    const originalSettings = { ...settings }; // Store original for revert
    const pathParts = path.split('.');
    
    // Validate path structure
    if (pathParts.length === 0) {
      throw new Error('Empty path provided to updateSetting');
    }
    
    const newSettings = { ...settings };
    let current = newSettings;
    
    try {
      // Navigate to the parent of the target property
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (!current[part] || typeof current[part] !== 'object') {
          current[part] = {};
        }
        current[part] = { ...current[part] };
        current = current[part];
      }
      
      // Set the value
      const finalKey = pathParts[pathParts.length - 1];
      current[finalKey] = value;
    } catch (error) {
      console.error('Error updating setting structure:', error);
      throw new Error('Failed to update setting: invalid path structure');
    }
    
    setSettings(newSettings);
    
    // Always persist setting changes to backend when authenticated
    if (isAuthenticated) {
      try {
        const response = await fetch(API_ENDPOINTS.SETTINGS, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ path: `settings.${path}`, value })
        });
        if (response.ok) {
          const data = await response.json();
          // Update with the response data to ensure consistency with backend
          if (data.settings) {
            setSettings(data.settings);
          }
        } else {
          console.error('Failed to update setting:', response.status);
          // Revert the setting on failure
          setSettings(originalSettings);
        }
      } catch (error) {
        console.error('Error updating setting:', error);
        // Revert on error
        setSettings(originalSettings);
      }
    }
  }, [isAuthenticated, settings, setSettings]);

  const updateReminderTracking = useCallback(async (reminderKey, timestamp) => {
    const updatedTracking = { ...reminderTracking, [reminderKey]: timestamp };
    setReminderTracking(updatedTracking);

    // Persist to backend when authenticated
    if (isAuthenticated) {
      try {
        await fetch(`${API_ENDPOINTS.SETTINGS}/reminders`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ reminderKey, timestamp: new Date(timestamp).toISOString() })
        });
      } catch (error) {
        console.error('Error updating reminder tracking:', error);
      }
    }
  }, [reminderTracking, isAuthenticated]);

  // Set settings locally without persisting to backend. Useful for
  // optimistic local updates where we don't want to trigger network
  // calls or global auth handlers.
  const setLocalSettings = useCallback((next) => {
    setSettings(prev => (typeof next === 'function' ? next(prev) : next));
  }, [setSettings]);

  const contextValue = useMemo(() => ({
    courses,
    assignments,
    notifications,
    settings,
    loading,
    setLocalSettings,
    addCourse,
    updateCourse,
    deleteCourse,
    addAssignment,
    updateAssignment,
    deleteAssignment,
    addNotification,
    removeNotification,
    updateSettings,
    updateSetting,
    updateReminderTracking,
    prefetchData,
    letterGradeToGPA: (grade) => letterGradeToGPA(grade, settings),
    gpaToLetterGrade: (grade) => gpaToLetterGrade(grade, settings),
    parseGrade,
    formatGrade
  }), [
    courses,
    assignments,
    notifications,
    settings,
    loading,
    addCourse,
    updateCourse,
    deleteCourse,
    addAssignment,
    updateAssignment,
    deleteAssignment,
    addNotification,
    removeNotification,
    updateSettings,
    updateSetting,
    updateReminderTracking,
    prefetchData,
    setLocalSettings,
    parseGrade,
    formatGrade
  ]);

  

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};
